// The "Active deals" KANBAN board, shared by two pipelines.
//
// It was written for the in-band OPPORTUNITIES family on the premise that "the money-bearing holds are
// single-stage (no status), so they can't form columns". THAT PREMISE WAS FALSE (corrected 2026-09-01):
// `ck_shows_status` has accepted offer|hold|confirmed|settled|cancelled since the shows table shipped, so a
// hold is one value in a five-value pipeline, not a category of its own. The cross-roster BOOKING board now
// passes `BOOKING_COLUMNS` and draws real shows; the per-artist board still reads opportunities. Hence
// `columns` — the caller owns its own pipeline, and this component owns only the rendering.
//
// Presentation-only and self-contained: every mode normalizes to `DealCard[]`. It never receives cents and
// never formats money — a caller that may show a figure passes it pre-formatted on `trailing`, so a money-free
// caller has no money slot at all rather than a nulled one. Never drops a card: an unknown status (loose
// fixture / future value) still gets its own column (the "always render" discipline).
//
// PRESENTATION-ONLY, INCLUDING ITS VERBS. The board owns no vocabulary of its own — a caller supplies each
// card's `actions` (the words and the handler) and its `onOpen` (what a click on the body does), exactly as it
// supplies `trailing` for money it never formats. What a given card CAN do is therefore the caller's decision,
// not this component's:
//   • The per-artist board (band context mounted) gives a HOLD card its three verbs — Confirm show, Release,
//     View details — that act in place, plus click-to-open its editor (ART-139).
//   • The cross-roster/scope board has no `BandProvider`, so its hold/confirmed cards carry no in-place verbs;
//     the card OPENS the show instead (`onOpen` → the per-artist roster show route), where those verbs live.
//   • An OFFER card carries Convert to hold / Pass where the band context exists, and opens the artist's
//     dashboard from the scope board otherwise.
// The board renders whatever each caller sets and knows nothing about what any of it means.
import { Plus } from "lucide-react";
import { Button, Card, EmptyState } from "@cntrl/ui";
import { dealStatusLabel, dealTypeLabel } from "./dealVocab.js";
import styles from "./DealsBoard.module.scss";

/** The pipeline column order shown even when empty (the CD "empty kanban read as intentional"). Terminal/unknown
 *  statuses are appended on-demand (see {@link columnsFor}). */
const BASE_COLUMNS = ["open", "confirmed", "completed"] as const;

/** Normalize a raw status to its column key (lower-cased; `canceled`→`cancelled`; unknown kept verbatim). */
function normStatus(status: string | null | undefined): string {
  const s = (status ?? "").trim().toLowerCase();
  if (s === "canceled") return "cancelled";
  return s === "" ? "open" : s;
}

/** A normalized board card — both modes map their source rows to this shape. */
export interface DealCard {
  /** Stable react key + (per-artist) the opportunityId used for the move mutation. */
  key: string;
  status: string | null | undefined;
  /** The opportunity `type` chip, rendered through `dealTypeLabel`. Optional — a booking card uses {@link tag}
   *  (the kind, rendered raw) instead, which takes precedence. */
  type?: string | null;
  /** Card headline — per-artist: the location/type; cross-roster: the artist name. */
  title: string;
  /** Secondary meta line (date · time, or type · date). */
  meta?: string | null;
  /** Small trailing line — per-artist invite tally ("2 in · 1 pending") / "You created". */
  detail?: string | null;
  /**
   * A right-aligned figure on the card's foot — the booking board's deal GUARANTEE, as legacy's card shows it.
   * Pre-formatted by the caller: the board never receives cents and never formats money, so a money-free
   * caller simply passes nothing and the row has no money slot at all.
   */
  trailing?: string | null;
  /**
   * Per-card verbs, rendered as a row under the foot. Presentation-only, like everything else here: the board
   * never names an action and never knows what one does. Omit for a card with nothing to do.
   */
  actions?: DealCardAction[];

  // ---- the ENRICHED hold card (ART-139) ------------------------------------------------------------------
  // A hold card surfaces more of the deal the row already stores, so it stops being three near-empty lines.
  // These are all optional and layer ON TOP of the plain shape above — an offer or confirmed card that sets
  // none of them renders exactly as before. Values are pre-formatted by the caller (the board formats nothing).

  /** A raw chip label shown top-left — the kind tag ("Show" / "Festival"). Takes precedence over {@link type},
   *  which goes through `dealTypeLabel`; a tag is a word we already have and want rendered verbatim. */
  tag?: string | null;
  /** Tints the {@link tag} chip by funnel state (ART-326): soft hold · waiting on artist · passed by team · hold ·
   *  ready to confirm. The words carry the state; the tint only groups it (WCAG 1.4.1). */
  tagTone?: "soft" | "wait" | "pass" | "hold" | "ready";
  /** Dashed outline — a soft hold the agency alone can see (design 4c). */
  dashed?: boolean;
  /** Tints {@link meta} — `"hot"` is the ≤3-days-left countdown (accent + bold), the spec's `bk-left--hot`. */
  metaTone?: "hot";
  /** The mono sub-line under the title — the hold card's "city, state · venue" line. `"—"` when unknown. */
  subtitle?: string | null;
  /** The labelled mini-cells row (Date · Amount · Buyer). A null `value` renders a muted em-dash — honest
   *  sparse, never a fabricated figure. Omit the array entirely for a card with no kv row. */
  kv?: DealKvCell[];
  /** The note strip — the deal's own free text. Omitted (no row) when absent, per honest-sparse rendering. */
  note?: string | null;
  /**
   * Opens this deal's detail — makes the card body a click target (part 3). The action buttons sit above it,
   * so a click on a verb never also opens the card. Omit for a non-interactive card.
   */
  onOpen?: () => void;
  /**
   * The order inside its column (ART-331): whichever comes SOONER, the show date or the Good through — a
   * `YYYY-MM-DD`. Cards with one sort ahead of cards without, by it; ties keep the caller's order.
   */
  sortKey?: string | null;
}

/** One labelled cell in a hold card's kv row. A null `value` renders the muted em-dash. */
export interface DealKvCell {
  label: string;
  value: string | null;
}

/**
 * One per-card verb. `tone: "quiet"` is the secondary of a pair (Pass beside Convert to hold).
 *
 * `align: "end"` pushes this verb — and everything after it — to the card's trailing edge, which is the
 * ShellTopbar cluster's `margin-inline-start: auto`, not a second layout mode. It exists for a verb that is
 * not part of the card's decision at all: "View details" reads an offer's notes and changes nothing, so
 * sitting in the row beside Convert and Pass would rank it with two choices it is not one of.
 */
export interface DealCardAction {
  label: string;
  onClick: () => void;
  /** `danger` is the red-outline destructive verb (Cancel show, ART-326). */
  tone?: "primary" | "quiet" | "danger";
  align?: "end";
  disabled?: boolean;
}

/**
 * The inline "add one of these" affordance at the FOOT of a column.
 *
 * Its own type rather than a reused {@link DealCardAction}: there is never a pair of these to rank, so
 * `tone` would be a knob that does nothing — and a prop that does nothing is one somebody eventually
 * believes in.
 */
export interface DealColumnAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

/** The columns to render: the always-on pipeline + any terminal/unknown status present, appended in a stable
 *  canonical-then-first-seen order so nothing is ever dropped. `base` lets a caller pin its OWN pipeline —
 *  the booking board passes offer→hold→confirmed — while keeping the never-drop guarantee for anything else. */
function columnsFor(cards: DealCard[], base: readonly string[]): string[] {
  const present = new Set(cards.map((c) => normStatus(c.status)));
  const cols: string[] = [...base];
  if (present.has("cancelled") && !cols.includes("cancelled")) cols.push("cancelled");
  for (const c of cards) {
    const s = normStatus(c.status);
    if (!cols.includes(s)) cols.push(s);
  }
  return cols;
}

export interface DealsBoardProps {
  cards: DealCard[];
  /** Region heading (default "Active deals"). */
  title?: string;
  /** "+ New offer" — omit to render a read-only board (cross-roster has no band context to create in). */
  onNewDeal?: () => void;
  /**
   * The pipeline columns rendered even when empty. Defaults to the OPPORTUNITY pipeline
   * (open/confirmed/completed) the per-artist board reads. The cross-roster BOOKING board passes
   * {@link BOOKING_COLUMNS} — a deal there is an `artist.shows` row, whose stages are offer/hold/confirmed.
   */
  columns?: readonly string[];
  /** Section sub-line under the title ("By stage · offer → hold → confirmed"). */
  subtitle?: string;
  /**
   * Render the columns even with zero cards, each showing its own per-column empty, instead of
   * collapsing the whole board into one empty state. The booking pipeline sets this: its three stages
   * ARE the information (legacy's "an empty kanban reads as intentional"), and an Offer column that is
   * absent rather than empty is indistinguishable from a stage nobody built. Off by default so the
   * per-artist opportunity board keeps the single empty state it ships today.
   */
  pinColumns?: boolean;
  /**
   * An inline create at the foot of ONE column, keyed by that column's status.
   *
   * Per-column rather than board-wide because only the first stage can be created INTO: a hold and a
   * confirmed show are `artist.shows` rows born from the editor, so an "Add hold" beside them would be a
   * button with nothing to do. Opt-in per caller for the same reason the header verb is — the cross-roster
   * board has no artist to create on, and its "+ New offer" jumps to the rail's artist search instead.
   *
   * The board still names no verb: the caller supplies the words and the handler, as it does for `actions`
   * and `trailing`.
   */
  columnActions?: Record<string, DealColumnAction>;
  /**
   * ART-331 — "Open calendar to see all {n} →" under each column: open the booking calendar filtered to that
   * column. The caller decides where that is (one artist's calendar, or the roster's).
   */
  onOpenColumn?: (column: string) => void;
  /** ART-331 — "View all" beside + New offer: the same calendar, unfiltered. */
  onViewAll?: () => void;
}

/** The booking pipeline (ART-326): Offer → Hold → Ready to confirm. A confirmed show leaves the board for the
 *  Shows list on the Booking tab. */
export const BOOKING_COLUMNS = ["offer", "hold", "ready"] as const;

/**
 * A column's own empty line. Legacy printed "No offers" / "No holds" / "No confirmeds"; the last is
 * ungrammatical, and the dev-reference spec for the same card writes "Nothing confirmed yet" — so the
 * first two are legacy verbatim and the third takes the spec's wording rather than its typo.
 *
 * Not `aria-hidden`: a column reading "No holds" is the answer to "how many holds are out?", and hiding
 * it left a screen-reader user with a heading, a count of 0, and nothing said about it. It stays even when
 * the column carries an inline create below it — "No offers" is the state, the button is the next step, and
 * a button alone answers neither question.
 */
function columnEmptyCopy(column: string): string {
  switch (column) {
    case "offer":
      return "No offers";
    case "hold":
      return "No holds";
    case "confirmed":
      return "Nothing confirmed yet";
    case "ready":
      return "Nothing ready to confirm";
    default:
      return `No ${dealStatusLabel(column).toLowerCase()} deals`;
  }
}

/**
 * The deals kanban (a self-contained card, drop it as a full-width child). Horizontally scrollable column
 * row; each column is a labelled region with its cards or a per-column empty hint. Colour is never the only
 * signal — every status/type is text-labelled (WCAG 1.4.1).
 *
 * The SHELL is `Card`, not a panel of its own (ART-115). It hand-rolled one — 16px padding, a 14px title,
 * a 16px radius — and every neighbour on both booking pages is a `Card` at 24px/16px/24px, so "Active deals"
 * rendered 8px left of and one size below every other heading on the page: measured 297px/14px against
 * 305px/16px for "Needs attention" directly above it. That is what a hand-rolled copy of a primitive costs,
 * and it is the case the component-library rule is about — the header slot here is exactly `Card`'s
 * title/subtitle/actions, so there was never a contract to fork for.
 */
export function DealsBoard({
  cards,
  title = "Active deals",
  onNewDeal,
  columns: base = BASE_COLUMNS,
  subtitle,
  pinColumns = false,
  columnActions,
  onOpenColumn,
  onViewAll,
}: DealsBoardProps) {
  const columns = columnsFor(cards, base);

  return (
    <Card
      id="deals-board"
      title={title}
      subtitle={subtitle}
      actions={
        (onNewDeal || onViewAll) && (
          <span className={styles.headActions}>
            {onViewAll && (
              <Button size="sm" variant="ghost" onClick={onViewAll}>
                View all
              </Button>
            )}
            {onNewDeal && (
              <Button size="sm" variant="ghost" onClick={onNewDeal}>
                <Plus size={16} aria-hidden /> New offer
              </Button>
            )}
          </span>
        )
      }
    >
      {cards.length === 0 && !pinColumns ? (
        <EmptyState
          title="No deals in the pipeline"
          description={
            onNewDeal
              ? "Deals you create appear here, grouped by stage. Start one with “New offer”."
              : "Deals across your booking roster appear here, grouped by stage."
          }
        />
      ) : (
        <div className={styles.board} role="list" aria-label={`${title} by stage`}>
          {columns.map((col) => {
            const colCards = sortCards(cards.filter((c) => normStatus(c.status) === col));
            const label = dealStatusLabel(col);
            const add = columnActions?.[col];
            return (
              <section
                key={col}
                className={styles.column}
                role="listitem"
                aria-label={`${label} (${colCards.length})`}
              >
                <header className={styles.columnHead}>
                  <span className={styles.columnDot} data-stage={col} aria-hidden />
                  <span className={styles.columnTitle}>{label}</span>
                  <span className={styles.columnCount}>{colCards.length}</span>
                </header>
                {colCards.length === 0 ? (
                  <p className={styles.columnEmpty}>{columnEmptyCopy(col)}</p>
                ) : (
                  <ul className={styles.cardList}>
                    {colCards.map((card) => (
                      <li key={card.key} className={styles.dealCard} data-dashed={card.dashed || undefined}>
                        {/* The card body is the click target (part 3). A stretched overlay button sits BEHIND
                            the content (z-index below it, `isolation: isolate` on the card), the
                            EditChatModal pattern — so text and the action verbs stay above it and a click on a
                            verb never also opens the card, without every button needing stopPropagation. */}
                        {card.onOpen && (
                          <button
                            type="button"
                            className={styles.dealOpen}
                            aria-label={`Open ${card.title}`}
                            onClick={card.onOpen}
                          />
                        )}
                        <div className={styles.dealCardTop}>
                          {/* The chip is the kind TAG when given (rendered raw), else the opportunity `type`
                              through `dealTypeLabel`. No tag and no type ⇒ NO chip: `dealTypeLabel` falls back
                              to "Deal", which on a board of deals says nothing, and a constant label is
                              decoration. */}
                          {(card.tag || card.type) && (
                            <span className={styles.dealChip} data-tone={card.tagTone}>
                              {card.tag ?? dealTypeLabel(card.type)}
                            </span>
                          )}
                          {card.meta && (
                            <span className={styles.dealMeta} data-tone={card.metaTone}>{card.meta}</span>
                          )}
                        </div>
                        <span className={styles.dealTitle} title={card.title}>{card.title}</span>
                        {card.subtitle && <span className={styles.dealSubtitle}>{card.subtitle}</span>}
                        {card.kv && card.kv.length > 0 && (
                          <div className={styles.dealKv}>
                            {card.kv.map((cell) => (
                              <div key={cell.label} className={styles.dealKvCell}>
                                <span className={styles.dealKvKey}>{cell.label}</span>
                                <span className={styles.dealKvVal} data-muted={cell.value == null || undefined}>
                                  {cell.value ?? "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {card.note && <p className={styles.dealNote}>{card.note}</p>}
                        {(card.detail || card.trailing) && (
                          <span className={styles.dealFoot}>
                            {card.detail && <span className={styles.dealDetail}>{card.detail}</span>}
                            {card.trailing && <span className={styles.dealTrailing}>{card.trailing}</span>}
                          </span>
                        )}
                        {card.actions && card.actions.length > 0 && (
                          <span className={styles.dealActions}>
                            {card.actions.map((a) => (
                              <button
                                key={a.label}
                                type="button"
                                className={styles.dealAction}
                                data-tone={a.tone ?? "primary"}
                                data-align={a.align}
                                disabled={a.disabled}
                                // The card names only its subject ("The Basement East"), so a bare "Convert to
                                // hold" is one of N identical buttons to a screen reader. The accessible name
                                // carries the row it acts on (WCAG 2.4.4), which the visible label cannot.
                                aria-label={`${a.label} — ${card.title}`}
                                onClick={a.onClick}
                              >
                                {a.label}
                              </button>
                            ))}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {onOpenColumn && colCards.length > 0 && (
                  <button type="button" className={styles.columnAll} onClick={() => onOpenColumn(col)}>
                    Open calendar to see all {colCards.length} →
                  </button>
                )}
                {add && (
                  <button
                    type="button"
                    className={styles.columnAdd}
                    disabled={add.disabled}
                    // The visible words are the caller's and say only the verb; the column they act on is
                    // the context a screen reader needs (WCAG 2.4.4), exactly as a card's verbs carry their
                    // subject. Cheap here and load-bearing the day a second column takes one.
                    aria-label={`${add.label} — ${label}`}
                    onClick={add.onClick}
                  >
                    <Plus size={14} aria-hidden /> {add.label}
                  </button>
                )}
              </section>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/** Sooner first by {@link DealCard.sortKey}; cards without one keep their order after those with one. */
function sortCards(cards: DealCard[]): DealCard[] {
  return cards
    .map((c, i) => ({ c, i }))
    .sort((a, b) => {
      const ka = a.c.sortKey ?? null;
      const kb = b.c.sortKey ?? null;
      if (ka && kb && ka !== kb) return ka < kb ? -1 : 1;
      if (ka && !kb) return -1;
      if (!ka && kb) return 1;
      return a.i - b.i;
    })
    .map((x) => x.c);
}
